import { Link } from '@tanstack/react-router';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { staffStageLabel, type PipelineTemplateView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { SearchInput } from '../../../../components/Input';
import { Select } from '../../../../components/Select';
import { formatDate } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { shownStagesOf } from '../StageBuilder';
import styles from './AdminSetup.module.css';

// Pipelines A: the pipelines on the left, each with its route as dots (filled = the stage is used), "Find a pipeline",
// and the arrow keys moving through them (06-qol §2). At laptop width and below, a drop-down above the editor instead.

const STEPS: Readonly<Record<string, 1 | -1>> = { ArrowDown: 1, ArrowUp: -1 };

export function familyLine(pipeline: Pick<PipelineTemplateView, 'roleFamily' | 'updatedAt'>): string {
  return `${pipeline.roleFamily ?? 'Any role family'} · updated ${formatDate(pipeline.updatedAt)}`;
}

/** Name or role family, ignoring capitals. */
export function matchesPipeline(pipeline: Pick<PipelineTemplateView, 'name' | 'roleFamily'>, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || pipeline.name.toLowerCase().includes(needle) || (pipeline.roleFamily ?? '').toLowerCase().includes(needle);
}

/** Each stage by the name the team uses, except that `offer` reads "Selected" while Offers is off (§L33). */
function RouteDots({ pipeline }: { readonly pipeline: PipelineTemplateView }): ReactNode {
  const isOffersOn = useModule('offers');
  const stages = shownStagesOf(pipeline.pipeline.stages, { isAssessmentsOn: useModule('assessments'), isOffersOn });
  const nameOf = (stage: (typeof stages)[number]): string => staffStageLabel(stage.stage, isOffersOn, stage.internalName.trim() || null);
  const route = stages.filter((stage) => stage.isActive).map(nameOf);
  return (
    <span className={styles.dots} role="img" aria-label={`Route: ${route.join(', ')}`}>
      {stages.map((stage) => (
        <span
          key={stage.stage}
          className={styles.dot}
          data-on={stage.isActive || undefined}
          title={`${nameOf(stage)} · ${stage.isActive ? 'used' : 'off'}`}
        />
      ))}
    </span>
  );
}

interface PipelineListProps {
  readonly pipelines: readonly PipelineTemplateView[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export function PipelineList({ pipelines, selectedId, onSelect }: PipelineListProps): ReactNode {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLUListElement | null>(null);
  const shown = pipelines.filter((pipeline) => matchesPipeline(pipeline, query));

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    const step = STEPS[event.key];
    if (step === undefined || shown.length === 0) return;
    event.preventDefault();
    const index = shown.findIndex((pipeline) => pipeline.id === selectedId);
    const next = shown[Math.min(shown.length - 1, Math.max(0, index + step))];
    if (!next || next.id === selectedId) return;
    listRef.current?.querySelectorAll<HTMLAnchorElement>('a')[shown.indexOf(next)]?.focus();
    onSelect(next.id);
  };

  return (
    <nav className={styles.pane} aria-label="Pipelines">
      <SearchInput aria-label="Find a pipeline" placeholder="Find a pipeline" value={query} onChange={(event) => setQuery(event.target.value)} />
      {shown.length === 0 ? (
        <div className={styles.empty}>
          <span>No pipeline matches “{query.trim()}”.</span>
          <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
            Clear
          </Button>
        </div>
      ) : (
        <ul ref={listRef} className={styles.list} onKeyDown={onKeyDown}>
          {shown.map((pipeline) => {
            const isSelected = pipeline.id === selectedId;
            return (
              <li key={pipeline.id}>
                <Link
                  to="/admin/pipelines/$pipelineId"
                  params={{ pipelineId: pipeline.id }}
                  className={styles.item}
                  aria-current={isSelected ? 'page' : undefined}
                  tabIndex={isSelected || (selectedId === null && pipeline === shown[0]) ? 0 : -1}
                >
                  <span className={styles.itemTop}>
                    <span className={styles.itemName}>{pipeline.name}</span>
                    {pipeline.isDefault && <Chip tone="progress">Default</Chip>}
                    <RouteDots pipeline={pipeline} />
                  </span>
                  <span className={styles.itemMeta}>{familyLine(pipeline)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <p className={styles.paneNote}>A job copies its pipeline when it is made. Editing here never moves a live job.</p>
    </nav>
  );
}

/** Laptop, tablet and phone: the pipeline is picked from a drop-down. */
export function PipelinePicker({ pipelines, selectedId, onSelect }: PipelineListProps): ReactNode {
  return (
    <div className={styles.picker}>
      <Select
        label="Pipeline"
        placeholder="New pipeline"
        value={selectedId}
        onChange={onSelect}
        options={pipelines.map((pipeline) => ({
          value: pipeline.id,
          label: pipeline.isDefault ? `${pipeline.name} · Default` : pipeline.name,
          description: familyLine(pipeline),
          searchText: `${pipeline.name} ${pipeline.roleFamily ?? ''}`,
        }))}
      />
    </div>
  );
}
